import { api, buildQueryParams, URLs } from '@/libs/api'
import type {
  ApiResponse,
  CreateUserEmployeePayload,
  CursorPaginatedResponse,
  Employee,
  EmployeeData,
  EmployeeListParams,
  EmployeeUser,
  InviteUserPayload,
} from '@/libs/interfaces'
import { IS_MOCK_MODE } from '@/libs/mocks/config'
import { employeesApiMock } from '@/libs/mocks/handlers/employees'

const employeesApiReal = {
  create: async (body: Partial<Employee>): Promise<Partial<Employee>> => {
    return await api
      .post<ApiResponse<Partial<Employee>>>(URLs.Employees.create(body.orgID || ''), body)
      .then((response) => response.data.data)
  },
  getByID: async (params: { orgID?: string; employeeID?: string }): Promise<Partial<Employee>> => {
    const { orgID, employeeID } = params
    return await api
      .get<ApiResponse<Partial<Employee>>>(URLs.Employees.byID(orgID ?? '', employeeID ?? ''))
      .then((response) => response.data.data)
  },
  getFromUserID: async (orgID: string, userID: string): Promise<EmployeeUser> => {
    return await api
      .get<ApiResponse<EmployeeUser>>(URLs.Employees.byUserID(orgID, userID))
      .then((response) => response.data.data)
  },
  list: async (params: EmployeeListParams): Promise<Employee[]> => {
    const { storeID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    return await api
      .get<ApiResponse<EmployeeData>>(URLs.Employees.list(storeID, query))
      .then((response) => response.data?.data.employees || [])
  },
  listCursor: async (params: EmployeeListParams): Promise<CursorPaginatedResponse<Employee>> => {
    const { storeID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<EmployeeData>>(URLs.Employees.list(storeID, query))
      .then((response) => response?.data?.data)

    return {
      data: response.employees,
      pageInfo: {
        first: response.first,
        prev: response.prev,
        cursor: response.cursor,
        next: response.next,
        last: response.last,
      },
    }
  },
  delete: async (params: { orgID?: string; employeeID?: string }): Promise<void> => {
    await api.delete(URLs.Employees.byID(params.orgID || '', params.employeeID || ''))
  },
  inviteUser: async (payload: InviteUserPayload): Promise<void> => {
    const { orgID, ...body } = payload
    await api.post(URLs.Users.create(orgID || ''), body)
  },
  createUserEmployee: async (orgID: string, payload: CreateUserEmployeePayload): Promise<void> => {
    await api.post(URLs.Users.createWithEmployee(orgID), payload)
  },
  update: async (payload: Partial<Employee>) => {
    const { orgID, ...patchData } = payload
    return await api
      .patch(URLs.Employees.byID(orgID || '', payload.employeeID || ''), patchData)
      .then((response) => response.data)
  },
}

export const employeesApi: typeof employeesApiReal = IS_MOCK_MODE
  ? employeesApiMock
  : employeesApiReal
